import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";

type Stop = {
  label: string;
  lat?: number | string | null;
  lng?: number | string | null;
  status?: string;
};

type Props = {
  stops: Stop[];
  destination: { label: string; lat?: number | string | null; lng?: number | string | null };
  driverLocation?: { lat: number; lng: number } | null;
  height?: number;
};

function toNum(v: number | string | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && (Math.abs(n) > 0.1) ? n : null;
}

// Numbered circle marker
function createNumberedIcon(num: number | string, color: string) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">${num}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function createDriverIcon() {
  return L.divIcon({
    className: "",
    html: `<div style="background:#2563eb;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4)">🚛</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

export function DeliveryRouteMap({ stops, destination, driverLocation, height = 200 }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [routeError, setRouteError] = useState(false);
  // The live driver marker is managed separately from the static layers so a
  // frequent position update just slides the marker instead of rebuilding the
  // map, re-fitting the viewport, and re-hitting the routing server.
  const driverMarkerRef = useRef<L.Marker | null>(null);
  const driverLocationRef = useRef(driverLocation);
  driverLocationRef.current = driverLocation;

  const validStops = stops
    .map((s, i) => ({ ...s, lat: toNum(s.lat), lng: toNum(s.lng), idx: i }))
    .filter((s) => s.lat !== null && s.lng !== null) as (Stop & { lat: number; lng: number; idx: number })[];

  const hasDriver = driverLocation != null;
  const destLat = toNum(destination.lat);
  const destLng = toNum(destination.lng);

  useEffect(() => {
    if (!mapRef.current) return;
    if (validStops.length === 0 && (destLat === null || destLng === null)) return;

    // Create map if not exists
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        zoomControl: true,
        attributionControl: false,
        scrollWheelZoom: false,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
      }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;
    const driver = driverLocationRef.current;

    // Clear existing layers except the tiles and the live driver marker (which
    // the separate effect below owns and slides).
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) return;
      if (layer === driverMarkerRef.current) return;
      map.removeLayer(layer);
    });

    // Add stop markers
    const allPoints: L.LatLng[] = [];
    for (const stop of validStops) {
      const color = stop.status === "picked_up" ? "#22c55e" : "#3b82f6";
      const icon = createNumberedIcon(stop.status === "picked_up" ? "✓" : stop.idx + 1, color);
      L.marker([stop.lat, stop.lng], { icon })
        .bindTooltip(stop.label, { direction: "top", offset: [0, -16] })
        .addTo(map);
      allPoints.push(L.latLng(stop.lat, stop.lng));
    }

    // Add destination marker
    if (destLat !== null && destLng !== null) {
      L.marker([destLat, destLng], { icon: createNumberedIcon(stops.length + 1, "#dc2626") })
        .bindTooltip(destination.label, { direction: "top", offset: [0, -18] })
        .addTo(map);
      allPoints.push(L.latLng(destLat, destLng));
    }

    if (driver) allPoints.push(L.latLng(driver.lat, driver.lng));

    // Fit bounds only here (on stop/destination changes), never on a live driver
    // move — otherwise the viewport would yank every few seconds.
    if (allPoints.length > 0) {
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
    }

    // Fetch route from OSRM. The route originates at the driver's position at
    // build time; it refreshes on stop transitions, not on every GPS tick.
    const coords = [
      ...(driver ? [`${driver.lng},${driver.lat}`] : []),
      ...validStops.filter((s) => s.status !== "picked_up").map((s) => `${s.lng},${s.lat}`),
      ...(destLat !== null && destLng !== null ? [`${destLng},${destLat}`] : []),
    ];

    if (coords.length >= 2) {
      const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${coords.join(";")}?overview=full&geometries=geojson`;
      fetch(osrmUrl)
        .then((r) => r.json())
        .then((data) => {
          // The map may have been destroyed while OSRM was responding —
          // drawing on it then throws inside this promise chain.
          if (mapInstanceRef.current !== map) return;
          if (data.routes?.[0]?.geometry) {
            const routeCoords = data.routes[0].geometry.coordinates.map(
              ([lng, lat]: [number, number]) => L.latLng(lat, lng)
            );
            L.polyline(routeCoords, {
              color: "#3b82f6",
              weight: 4,
              opacity: 0.8,
            }).addTo(map);
            setRouteError(false);
          }
        })
        .catch(() => {
          if (mapInstanceRef.current !== map) return;
          setRouteError(true);
          // Fallback: draw straight lines
          const lineCoords = [
            ...(driver ? [L.latLng(driver.lat, driver.lng)] : []),
            ...validStops.filter((s) => s.status !== "picked_up").map((s) => L.latLng(s.lat, s.lng)),
            ...(destLat !== null && destLng !== null ? [L.latLng(destLat, destLng)] : []),
          ];
          if (lineCoords.length >= 2) {
            L.polyline(lineCoords, { color: "#3b82f6", weight: 3, opacity: 0.5, dashArray: "8 4" }).addTo(map);
          }
        });
    }

    return () => {
      // Don't destroy — reuse the map instance
    };
    // hasDriver is a dep so the first GPS fix redraws the route from the
    // driver; later fixes only slide the marker.
  }, [validStops.length, destLat, destLng, stops.map((s) => s.status).join(","), hasDriver]);

  // Slide (or first-create) the live driver marker without rebuilding the map.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !driverLocation) return;
    if (driverMarkerRef.current) {
      driverMarkerRef.current.setLatLng([driverLocation.lat, driverLocation.lng]);
    } else {
      driverMarkerRef.current = L.marker([driverLocation.lat, driverLocation.lng], {
        icon: createDriverIcon(),
      })
        .bindTooltip("Driver", { direction: "top", offset: [0, -18] })
        .addTo(map);
    }
  }, [driverLocation?.lat, driverLocation?.lng]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      driverMarkerRef.current = null;
    };
  }, []);

  if (validStops.length === 0 && (destLat === null || destLng === null)) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
        <MapPin className="mx-auto mb-2 h-5 w-5" />
        No location data available for route map
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-lg border" style={{ height, zIndex: 0 }}>
      <div ref={mapRef} className="h-full w-full" />
    </div>
  );
}
